/**
 * Возраст кредита: сколько времени прошло с выдачи.
 *
 * Дата выдачи нужна для одного вопроса, который человек задаёт вслух: свежий
 * ли это долг или взятый давно. Автокредит на 400 000 ₽, выданный в прошлом
 * месяце, и ипотека с тем же остатком на двадцатом году — разные истории, и
 * остаток долга их не различает.
 *
 * **Здесь считается только возраст.** Календарные месяцы с выдачи — это не
 * «пройденные платежи»: приложение не знает ни даты первого платежа, ни
 * пропусков, ни каникул, ни досрочных взносов. Кредит, выданный 30 августа с
 * первым платежом 10 сентября, к 11 сентября прошёл один платёж и ноль полных
 * месяцев — и наоборот. Называть одно другим значит выдавать календарь за
 * историю банка.
 *
 * **Чего здесь нет.** Восстановления исходной суммы долга и уже уплаченных
 * процентов. Это обратная арифметика по предположению «платёж и ставка не
 * менялись, досрочных взносов не было», и её результат неотличим на вид от
 * выписки банка. Пока таких фактов у приложения нет, оно о них молчит.
 *
 * **И зачем это в расчётах не участвует.** Уплаченного не вернуть, поэтому
 * выбор, какой долг гасить первым, считается по будущему (`payoffEffect`) и
 * от даты выдачи не зависит.
 */
import { isAfter, parts, type PlainDate } from './date.js';
import type { Loan } from './types.js';

export interface LoanProgress {
  /** Сколько полных месяцев прошло с выдачи. `null` — дата неизвестна. */
  monthsSinceOpened: number | null;
  /** Сколько платежей осталось по расчёту графика. */
  monthsLeft: number | null;
  /** Чего не хватает. Пусто — известно всё, что здесь обещано. */
  missing: string[];
  /** Данные противоречат друг другу: считать по ним нельзя. */
  conflicts: string[];
}

/** Полных месяцев между датами. Неполный месяц не считается. */
export function monthsBetween(from: PlainDate, to: PlainDate): number {
  const a = parts(from);
  const b = parts(to);
  const months = (b.year - a.year) * 12 + (b.month - a.month);
  // 30 сентября → 1 октября это один день, а не месяц.
  return b.day >= a.day ? months : months - 1;
}

/**
 * Возраст кредита и остаток графика.
 *
 * `monthsLeft` передаётся снаружи — его считает `amortize`. Второй, чуть
 * другой арифметике того же графика здесь взяться неоткуда.
 */
export function loanProgress(input: {
  loan: Loan;
  today: PlainDate;
  /** Сколько платежей осталось — из расчёта графика. */
  monthsLeft?: number | null;
}): LoanProgress {
  const { loan, today } = input;
  const monthsLeft = input.monthsLeft ?? null;
  const missing: string[] = [];
  const conflicts: string[] = [];

  if (!loan.openedAt) {
    missing.push('дата выдачи');
    return { monthsSinceOpened: null, monthsLeft, missing, conflicts };
  }

  // Дата, на которую известен остаток: возраст считаем до неё. Без неё —
  // до сегодня.
  const asOf = loan.principalAsOf ?? today;

  // Противоречие, а не мелочь: остаток, названный до выдачи кредита, означает
  // ошибку в одной из дат. Считать по ним — выдать уверенное враньё.
  if (isAfter(loan.openedAt, asOf)) {
    conflicts.push('Остаток назван на дату раньше выдачи кредита — поправь одну из дат');
    return { monthsSinceOpened: null, monthsLeft, missing, conflicts };
  }

  return {
    monthsSinceOpened: Math.max(0, monthsBetween(loan.openedAt, asOf)),
    monthsLeft,
    missing,
    conflicts,
  };
}

/**
 * Возраст кредита словами.
 *
 * Отдельно, потому что произносится в двух местах — в списке долгов и на
 * экране кредита. Две формулировки одного числа человек читает как два разных
 * факта.
 *
 * Слово «платежей» здесь не употребляется намеренно: приложение знает
 * календарь, а не историю оплат.
 */
export function progressLabel(p: LoanProgress): string | null {
  if (p.conflicts.length > 0) return p.conflicts[0]!;
  if (p.monthsSinceOpened === null) return null;
  return `С выдачи прошло ${p.monthsSinceOpened} мес`;
}

/** Остаток графика, если он посчитан: сколько ещё платежей. */
export const leftLabel = (p: LoanProgress): string | null =>
  (p.monthsLeft === null ? null : `Осталось ${p.monthsLeft} платежей`);
